import { LOG_TYPES } from '../constants/logTypeRegistry';
import {
  CARE_BEHAVIOR_QUICK_PATTERN,
  CARE_FOOD_REFUSAL_PATTERN,
  CARE_FOOD_SIGNAL_PATTERN,
  CARE_MEDICATION_QUICK_PATTERN,
  CARE_QUICK_CAPTURE_STRONG_ACTIVITY_PATTERN,
  CARE_QUICK_CAPTURE_SUMMARY_SKIP_PATTERN,
  CARE_QUICK_CAPTURE_WEAK_ACTIVITY_PATTERNS,
  CARE_SLEEP_QUICK_PATTERN,
  CARE_TOILETING_SIGNAL_PATTERN,
} from '../shared/careSignalPatterns';
import { extractQuickNoteEntities } from './quickNoteClassification';
import {
  isLongNoteEmailText,
  stripLongNoteEmailBoilerplate,
} from '../features/careSourceProcessing/parsing';
import { detectNaturalCaptureInputType } from '../components/Dashboard/ImportLogs/importLogsPipeline';
import { hasCareExplicitTime } from '../shared/careDateTimePatterns';

const QUICK_CAPTURE_EMAIL_GREETINGS = /^(?:hi|hello|good morning|good afternoon)\b/i;
const QUICK_CAPTURE_UPDATE_PHRASE = /\bquick update\b/i;
const QUICK_CAPTURE_MEDICATION = CARE_MEDICATION_QUICK_PATTERN;
const QUICK_CAPTURE_DURATION = /\b\d+(?:\.\d+)?\s*(?:hours?|minutes?|mins?|hrs?)\b/i;
const QUICK_CAPTURE_REFUSAL = CARE_FOOD_REFUSAL_PATTERN;
const QUICK_CAPTURE_SLEEP = CARE_SLEEP_QUICK_PATTERN;
const QUICK_CAPTURE_FOOD = CARE_FOOD_SIGNAL_PATTERN;
const QUICK_CAPTURE_BEHAVIOR = CARE_BEHAVIOR_QUICK_PATTERN;
const QUICK_CAPTURE_TOILETING = CARE_TOILETING_SIGNAL_PATTERN;

const QUICK_NOTE_CATEGORY_PRECEDENCE = [
  'behavior',
  'medication',
  'food',
  'sleep',
  'activity',
  'mood',
  'log',
];

export const resolveMergedQuickNoteCategory = (entities = [], fallbackCategory = 'log') => {
  const detectedCategories = Array.isArray(entities)
    ? entities.map((entity) => String(entity?.category || '').trim().toLowerCase()).filter(Boolean)
    : [];

  for (const category of QUICK_NOTE_CATEGORY_PRECEDENCE) {
    if (detectedCategories.includes(category)) {
      return category;
    }
  }

  return fallbackCategory || 'log';
};

const countQuickCaptureDomains = (text = '') => {
  const rawText = String(text || '').trim();
  if (!rawText) {
    return 0;
  }

  const domains = [
    QUICK_CAPTURE_SLEEP.test(rawText),
    QUICK_CAPTURE_FOOD.test(rawText),
    QUICK_CAPTURE_BEHAVIOR.test(rawText),
    QUICK_CAPTURE_MEDICATION.test(rawText),
    QUICK_CAPTURE_TOILETING.test(rawText),
  ].filter(Boolean);

  return domains.length;
};

export const getQuickLogRoutingPreview = (text = '') => {
  const rawText = String(text || '').trim();
  if (!rawText) {
    return {
      route: 'review_needed',
      reason: 'empty_or_unclear',
      signals: ['empty_text'],
      shouldUseBackendExtraction: false,
    };
  }

  const signals = [];
  const hasEmailStyleSignal = QUICK_CAPTURE_EMAIL_GREETINGS.test(rawText);
  const hasQuickUpdateSignal = QUICK_CAPTURE_UPDATE_PHRASE.test(rawText);
  const hasMultipleSentences = rawText.split(/(?<=[.!?])\s+/).filter(Boolean).length > 1;
  const hasMedicationSignal = QUICK_CAPTURE_MEDICATION.test(rawText);
  const hasTimeSignal = hasCareExplicitTime(rawText);
  const hasDurationSignal = QUICK_CAPTURE_DURATION.test(rawText);
  const hasRefusalSignal = QUICK_CAPTURE_REFUSAL.test(rawText);
  const hasMultipleDomains = countQuickCaptureDomains(rawText) > 1;
  const hasToiletingSignal = QUICK_CAPTURE_TOILETING.test(rawText);
  const hasMultipleEventsSignal = /(?:\b(?:and|but|later)\b|,)/i.test(rawText);
  const isShortSimpleNote = rawText.length <= 120
    && !hasEmailStyleSignal
    && !hasQuickUpdateSignal
    && !hasMultipleSentences
    && !hasMedicationSignal
    && !hasTimeSignal
    && !hasDurationSignal
    && !hasRefusalSignal
    && !hasMultipleDomains
    && !hasMultipleEventsSignal;

  if (hasEmailStyleSignal) signals.push('email_style');
  if (hasQuickUpdateSignal) signals.push('quick_update');
  if (hasMultipleSentences) signals.push('multiple_sentences');
  if (hasMultipleEventsSignal) signals.push('multiple_events');
  if (hasMedicationSignal) signals.push('medication');
  if (hasTimeSignal) signals.push('time');
  if (hasDurationSignal) signals.push('duration');
  if (hasRefusalSignal) signals.push('refusal');
  if (hasMultipleDomains) signals.push('multi_domain');
  if (hasToiletingSignal) signals.push('toileting');

  if (
    hasMedicationSignal
    || hasTimeSignal
    || hasDurationSignal
    || hasRefusalSignal
    || hasEmailStyleSignal
    || hasQuickUpdateSignal
    || hasMultipleSentences
    || hasMultipleDomains
    || hasMultipleEventsSignal
    || (hasToiletingSignal && hasMultipleEventsSignal)
    || rawText.length > 120
  ) {
    let reason = 'rich_narrative';
    if (hasMedicationSignal) {
      reason = 'high_risk_medication';
    } else if (hasTimeSignal || hasDurationSignal) {
      reason = 'time_or_duration';
    } else if (hasMultipleDomains) {
      reason = 'multi_domain';
    } else if (hasToiletingSignal && hasMultipleEventsSignal) {
      reason = 'toileting_complex';
    }

    return {
      route: 'claude',
      reason,
      signals,
      shouldUseBackendExtraction: true,
    };
  }

  if (hasToiletingSignal || isShortSimpleNote) {
    return {
      route: 'rule_only',
      reason: 'short_simple_note',
      signals,
      shouldUseBackendExtraction: false,
    };
  }

  return {
    route: 'review_needed',
    reason: 'empty_or_unclear',
    signals: signals.length > 0 ? signals : ['unclear_text'],
    shouldUseBackendExtraction: false,
  };
};

export const isQuickLogLongNoteText = (text = '') => {
  const rawText = String(text || '').trim();
  if (!rawText) {
    return false;
  }

  const normalizedText = rawText.replace(/\r\n/g, '\n');
  const paragraphs = normalizedText.split(/\n{2,}/).map((paragraph) => paragraph.trim()).filter(Boolean);
  const words = rawText.split(/\s+/).filter(Boolean);
  const sentenceCount = rawText.split(/(?<=[.!?])\s+/).filter(Boolean).length;
  const hasMultiSectionLanguage = /\b(?:food|breakfast|lunch|dinner|snack|behavior|mood|medication|sleep|activity|school|teacher|recess|art class)\b/i.test(rawText)
    && paragraphs.length > 1;

  return Boolean(
    paragraphs.length > 1
    || words.length >= 90
    || rawText.length >= 450
    || sentenceCount >= 8
    || hasMultiSectionLanguage
  );
};

export const getQuickLogRoutingDecision = (text = '') => {
  const rawText = String(text || '').trim();
  const routingPreview = getQuickLogRoutingPreview(rawText);
  const captureState = getQuickNoteCaptureState(rawText);
  const entityCount = captureState.entityCount || 0;
  const hasLongNoteStructure = isQuickLogLongNoteText(rawText);
  const hasStructuredEventSignals = [
    'multiple_sentences',
    'multiple_events',
    'multi_domain',
  ].some((signal) => routingPreview.signals.includes(signal));

  let mode = 'simple_local';
  let reason = rawText ? 'single_event_high_confidence' : 'empty_note';

  if (!rawText) {
    mode = 'simple_local';
    reason = 'empty_note';
  } else if (routingPreview.route === 'review_needed' && entityCount > 0) {
    mode = 'small_structured_review';
    reason = 'structured_event_signals';
  } else if (hasLongNoteStructure) {
    mode = 'long_note_review';
    reason = isLongNoteEmailText(rawText) ? 'long_note_email' : 'long_narrative';
  } else if (entityCount > 1 || hasStructuredEventSignals) {
    mode = 'small_structured_review';
    reason = entityCount > 1 ? 'multiple_local_events' : 'structured_event_signals';
  } else if (routingPreview.route === 'review_needed' && entityCount === 0) {
    mode = 'ai_smart_capture';
    reason = 'low_confidence';
  }

  const localConfidence = mode === 'ai_smart_capture'
    ? 'low'
    : mode === 'small_structured_review' || mode === 'long_note_review'
      ? 'high'
      : (entityCount > 0 ? 'high' : 'medium');
  const inputType = detectNaturalCaptureInputType({
    source: 'quick_log',
    route: mode === 'ai_smart_capture' ? 'claude' : 'rule_only',
    isLongNote: hasLongNoteStructure,
    hasStructuredEvents: hasStructuredEventSignals || entityCount > 1,
    hasStrongSimpleSignal: mode === 'simple_local',
  });

  return {
    mode,
    reason,
    localConfidence,
    inputType,
    eventCount: entityCount,
    routingPreview,
    captureState,
    longNoteDetected: hasLongNoteStructure,
    shouldUseBackendExtraction: mode === 'ai_smart_capture'
      || routingPreview.reason === 'high_risk_medication',
    shouldReviewInApp: mode === 'small_structured_review' || mode === 'long_note_review',
    shouldSaveLocally: mode === 'simple_local',
  };
};

export const getQuickLogPreAiDecision = (text = '') => getQuickLogRoutingDecision(text);

export const shouldUseQuickLogSmartCapture = (text = '') => (
  getQuickLogPreAiDecision(text).shouldUseBackendExtraction
);

const getQuickLogWordCount = (text = '') => String(text || '').trim().split(/\s+/).filter(Boolean).length;

export const shouldRouteShortEmailUpdateToCaptureFirst = ({
  originalText = '',
  routingDecision = {},
} = {}) => {
  const rawText = String(originalText || '').trim();
  if (!rawText || !rawText.includes('\n')) {
    return false;
  }

  const mode = String(routingDecision?.mode || '').trim();
  if (mode !== 'small_structured_review') {
    return false;
  }

  if (!isLongNoteEmailText(rawText)) {
    return false;
  }

  const strippedText = stripLongNoteEmailBoilerplate(rawText);
  const strippedWordCount = getQuickLogWordCount(strippedText);
  const strippedParagraphCount = strippedText
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean).length;
  const boilerplateWasRemoved = strippedText.trim() !== rawText.trim();

  return Boolean(
    boilerplateWasRemoved
    && strippedWordCount >= 12
    && strippedWordCount < 90
    && strippedText.length < 450
    && strippedParagraphCount <= 4
  );
};

const getLargestEntityCoverage = (entities = [], sourceText = '') => {
  const sourceWordCount = getQuickLogWordCount(sourceText);
  if (!sourceWordCount || !Array.isArray(entities) || entities.length === 0) {
    return 0;
  }

  const largestEntityWordCount = entities.reduce((largest, entity) => {
    const entityWordCount = getQuickLogWordCount(entity?.text || '');
    return Math.max(largest, entityWordCount);
  }, 0);

  return largestEntityWordCount / sourceWordCount;
};

export const shouldEscalateQuickStructuredReviewToAi = ({
  text = '',
  transcriptNormalization = {},
  structureDecision = {},
  captureState = {},
} = {}) => {
  const rawText = String(text || '').trim();
  if (!rawText) {
    return false;
  }

  const transcriptReasons = Array.isArray(transcriptNormalization?.reasons)
    ? transcriptNormalization.reasons
    : [];
  const structureReasons = Array.isArray(structureDecision?.reasons)
    ? structureDecision.reasons
    : [];
  const entities = Array.isArray(captureState?.entities) ? captureState.entities : [];
  const entityCategories = new Set(entities.map((entity) => entity?.category).filter(Boolean));
  const candidateSegments = Array.isArray(structureDecision?.candidateSegments)
    ? structureDecision.candidateSegments
    : [];
  const wordCount = getQuickLogWordCount(rawText);
  const largestEntityCoverage = getLargestEntityCoverage(entities, rawText);
  const hasMixedEventSignals = transcriptReasons.includes('multiple_categories')
    || structureReasons.includes('multiple_event_signals')
    || structureReasons.includes('time_shifts')
    || structureDecision?.structureType === 'multiple_events';
  const looksLikeDictatedNarrative = Boolean(transcriptNormalization?.isLikelyDictated)
    || wordCount >= 28
    || structureReasons.includes('time_shifts');
  const wasForcedToReview = Boolean(transcriptNormalization?.shouldForceReview)
    || ['multiple_events', 'unclear'].includes(structureDecision?.structureType);

  if (!hasMixedEventSignals || !looksLikeDictatedNarrative || !wasForcedToReview) {
    return false;
  }

  const missingLocalSplit = entities.length <= 1
    || (candidateSegments.length >= 2 && entities.length < Math.min(candidateSegments.length, 2));
  const collapsedIntoSingleCategory = entities.length > 0
    && entityCategories.size <= 1
    && largestEntityCoverage >= 0.65;

  return missingLocalSplit || collapsedIntoSingleCategory;
};

export const getQuickNoteCaptureState = (text = '') => {
  const rawEntities = extractQuickNoteEntities(text);
  const captureableEntities = rawEntities.filter((entity) => {
    const normalized = String(entity?.text || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!normalized) {
      return false;
    }

    if (CARE_QUICK_CAPTURE_SUMMARY_SKIP_PATTERN.test(normalized)) {
      return false;
    }

    if (entity?.category !== 'activity') {
      return true;
    }

    if (CARE_QUICK_CAPTURE_STRONG_ACTIVITY_PATTERN.test(normalized)) {
      return true;
    }

    return !CARE_QUICK_CAPTURE_WEAK_ACTIVITY_PATTERNS.some((pattern) => pattern.test(normalized));
  });

  const entityCount = captureableEntities.length;
  const labels = [...new Set(captureableEntities.map((entity) => entity?.label || LOG_TYPES[entity.category]?.displayLabel || entity.category))];

  return {
    entities: captureableEntities,
    entityCount,
    labels,
    category: resolveMergedQuickNoteCategory(captureableEntities),
    hasCaptureableEntries: entityCount > 0,
    captureActionLabel: entityCount > 0
      ? (entityCount > 1 ? `Capture ${entityCount} events` : 'Capture as event')
      : '',
  };
};
